import {
  Group, Stack, TextInput, Switch, SegmentedControl, MultiSelect, Table, Text, Box, Code, Button, Badge,
} from '@mantine/core';
import { IconInbox } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode, type KeyboardEvent } from 'react';
import type { CardType, ChargeState, WorklistRow, WorklistState } from '@taksikortti/shared';
import { WORKLIST_STATES } from '@taksikortti/shared';
import { useWorklist } from '../api/worklist';
import { useImportsList } from '../api/imports';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { StateBadge } from '../components/StateBadge';
import { ChargeStateBadge } from '../components/ChargeStateBadge';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { TableSkeleton } from '../components/TableSkeleton';
import { t } from '../lib/strings';
import { formatDateTime } from '../lib/dates';

const PAGE_SIZE = 100;

type SortKey = 'cardType' | 'korttinumero' | 'asiakas' | 'charges' | 'state' | 'invoiceState' | 'paymentState' | 'createdAt' | 'firstAt' | 'lastAt';

/** Fallback freshness window when no ImportLog exists (fresh env / month rollover). */
const NEW_FALLBACK_DAYS = 7;

/**
 * "Uusi" = arrived in the latest import batch AND untouched by any operator.
 * Fresh: createdAt >= latest import's startedAt (fallback: within 7 days).
 * Untouched:
 *  - case: still 'open', no comment, AND updatedAt ≈ createdAt (any trip/case
 *    action bumps updatedAt even when the case-level status stays 'open');
 *  - duplicate: still 'pending' with no comment (dup updatedAt bumps on every
 *    import refresh, so it can't be used as the touch signal there).
 */
const TOUCH_EPSILON_MS = 5_000; // creation writes createdAt+updatedAt in one go
function isNew(r: WorklistRow, newSince: string | Date | null): boolean {
  if (!r.createdAt) return false;
  const created = new Date(r.createdAt).getTime();
  const fresh = newSince
    ? created >= new Date(newSince).getTime()
    : Date.now() - created < NEW_FALLBACK_DAYS * 24 * 3600_000;
  if (!fresh) return false;
  if (r.kind === 'case') {
    const updated = r.updatedAt ? new Date(r.updatedAt).getTime() : created;
    return r.status === 'open' && !r.comment && updated - created < TOUCH_EPSILON_MS;
  }
  return r.status === 'pending' && !r.comment;
}

const CARD_TYPE_OPTIONS: { value: CardType; label: string }[] = [
  { value: 'kertakortti', label: t.cardType.kertakortti },
  { value: 'taksikortti', label: t.cardType.taksikortti },
  { value: 'taksikortti_plus', label: t.cardType.taksikortti_plus },
];

const STATE_OPTIONS: { value: WorklistState; label: string }[] =
  WORKLIST_STATES.map((s) => ({ value: s, label: t.state[s] }));

// Order = "handle first" priority; approved (billed/paid soon) at top.
const CHARGE_STATE_OPTIONS: { value: ChargeState; label: string }[] = [
  { value: 'approved', label: t.chargeState.approved },
  { value: 'active', label: t.chargeState.active },
  { value: 'pending', label: t.chargeState.pending },
  { value: 'blocked', label: t.chargeState.blocked },
];

interface WorklistSearch {
  q?: string;
  cardType?: CardType[];
  kind?: 'all' | 'case' | 'duplicate';
  showDone?: boolean;
  state?: WorklistState[];
  invoiceState?: ChargeState[];
  paymentState?: ChargeState[];
  sortKey?: SortKey;
  sortDir?: 'asc' | 'desc';
  page?: number;
}

export function WorklistPage() {
  const navigate = useNavigate({ from: '/cases' });
  const search = useSearch({ from: '/cases' }) as WorklistSearch;

  const [q, setQ] = useState(search.q ?? '');
  // Last q value WE wrote to the URL — distinguishes our own debounce echo from
  // an external URL change (nav re-click, deep link).
  const lastWrittenQ = useRef(search.q ?? '');
  // Debounce search → URL (replace, so typing doesn't spam history).
  useEffect(() => {
    const h = setTimeout(() => {
      if ((search.q ?? '') !== q) {
        lastWrittenQ.current = q;
        void navigate({ search: (p) => ({ ...p, q: q || undefined, page: undefined }), replace: true });
      }
    }, 300);
    return () => clearTimeout(h);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sync the input when the URL q changes EXTERNALLY — otherwise the box shows
  // stale text while results are unfiltered. Skips our own echo so it never
  // clobbers text typed during the debounce window.
  useEffect(() => {
    const urlQ = search.q ?? '';
    if (urlQ !== lastWrittenQ.current) {
      lastWrittenQ.current = urlQ;
      setQ(urlQ);
    }
  }, [search.q]);

  const cardTypes = search.cardType ?? [];
  const kind = search.kind ?? 'all';
  const showDone = search.showDone ?? false;
  const stateSel = search.state ?? [];
  const invoiceState = search.invoiceState ?? [];
  const paymentState = search.paymentState ?? [];
  const sortKey = search.sortKey;
  const sortDir = search.sortDir ?? 'asc';
  const page = search.page ?? 1;

  const { data, isLoading, isError, error, refetch } = useWorklist({
    q: search.q, cardType: cardTypes, kind, showDone, state: stateSel, invoiceState, paymentState, sortKey, sortDir, page, pageSize: PAGE_SIZE,
  });
  // Latest import's start time — the "Uusi" freshness threshold.
  const imports = useImportsList();
  const newSince = imports.data?.rows?.[0]?.startedAt ?? null;

  function patch(p: Partial<WorklistSearch>): void {
    void navigate({ search: (prev) => ({ ...prev, ...p }), replace: true });
  }
  function toggleSort(key: SortKey): void {
    if (sortKey === key) patch({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    else patch({ sortKey: key, sortDir: 'asc' });
  }
  function openRow(r: WorklistRow): void {
    if (r.kind === 'case' && r.caseId) {
      void navigate({ to: '/cases/$korttinumero/$caseId', params: { korttinumero: r.korttinumero, caseId: r.caseId } });
    } else if (r.groupId) {
      void navigate({ to: '/cases/tupla/$korttinumero/$groupId', params: { korttinumero: r.korttinumero, groupId: r.groupId } });
    }
  }

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const anyFilter = !!(search.q || cardTypes.length || stateSel.length || invoiceState.length ||
    paymentState.length || kind !== 'all' || showDone);

  function resetFilters(): void {
    setQ('');
    void navigate({ search: {}, replace: true });
  }

  // --- One-click triage presets: each sets the state/invoice filter bundle. ---
  type Quick = { key: keyof typeof t.worklist.quick; patch: Partial<WorklistSearch>; active: boolean };
  const only = (arr: WorklistState[] | ChargeState[], v: string): boolean => arr.length === 1 && arr[0] === v;
  const QUICK: Quick[] = [
    {
      key: 'open',
      patch: { state: ['avoin'], invoiceState: undefined, paymentState: undefined, showDone: undefined },
      active: only(stateSel, 'avoin') && !invoiceState.length && !paymentState.length,
    },
    {
      key: 'review',
      patch: { state: ['tarkistettava'], invoiceState: undefined, paymentState: undefined, showDone: undefined },
      active: only(stateSel, 'tarkistettava') && !invoiceState.length && !paymentState.length,
    },
    {
      key: 'soon',
      patch: { state: undefined, invoiceState: ['approved'], paymentState: undefined, showDone: undefined },
      active: only(invoiceState, 'approved') && !stateSel.length && !paymentState.length,
    },
    {
      key: 'done',
      patch: { state: ['valmis'], invoiceState: undefined, paymentState: undefined, showDone: undefined },
      active: only(stateSel, 'valmis') && !invoiceState.length && !paymentState.length,
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader title={t.worklist.title} subtitle={t.worklist.subtitle} />

      <Group gap="xs">
        {QUICK.map((qv) => (
          <Button
            key={qv.key}
            size="compact-sm"
            variant={qv.active ? 'filled' : 'light'}
            onClick={() => patch({ ...qv.patch, page: undefined })}
          >
            {t.worklist.quick[qv.key]}
          </Button>
        ))}
      </Group>

      <Group gap="md" align="end">
        <TextInput
          label={t.worklist.filters.search}
          placeholder={t.worklist.filters.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          w={300}
        />
        <MultiSelect
          label={t.worklist.filters.cardType}
          data={CARD_TYPE_OPTIONS}
          value={cardTypes}
          onChange={(v) => patch({ cardType: v.length ? (v as CardType[]) : undefined, page: undefined })}
          clearable
          w={230}
        />
        <MultiSelect
          label={t.worklist.columns.status}
          data={STATE_OPTIONS}
          value={stateSel}
          onChange={(v) => patch({ state: v.length ? (v as WorklistState[]) : undefined, page: undefined })}
          clearable
          w={220}
        />
        <MultiSelect
          label={t.worklist.filters.invoiceState}
          data={CHARGE_STATE_OPTIONS}
          value={invoiceState}
          onChange={(v) => patch({ invoiceState: v.length ? (v as ChargeState[]) : undefined, page: undefined })}
          clearable
          w={200}
        />
        <MultiSelect
          label={t.worklist.filters.paymentState}
          data={CHARGE_STATE_OPTIONS}
          value={paymentState}
          onChange={(v) => patch({ paymentState: v.length ? (v as ChargeState[]) : undefined, page: undefined })}
          clearable
          w={200}
        />
        <Box>
          <Text size="sm" fw={500} mb={4}>{t.worklist.filters.kind}</Text>
          <SegmentedControl
            aria-label={t.worklist.filters.kind}
            value={kind}
            onChange={(v) => patch({ kind: v as WorklistSearch['kind'], page: undefined })}
            data={[
              { value: 'all', label: t.worklist.filters.all },
              { value: 'case', label: t.worklist.kind.case },
              { value: 'duplicate', label: t.worklist.kind.duplicate },
            ]}
          />
        </Box>
        <Switch
          label={t.worklist.filters.showDone}
          checked={showDone}
          onChange={(e) => patch({ showDone: e.currentTarget.checked || undefined, page: undefined })}
        />
      </Group>

      {isLoading && <TableSkeleton rows={10} />}
      {isError && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && rows.length === 0 && (
        <EmptyState
          icon={<IconInbox size={28} />}
          title={anyFilter ? t.worklist.emptyNoMatches : t.worklist.empty}
          {...(anyFilter ? { action: { label: t.worklist.filters.clear, onClick: resetFilters } } : {})}
        />
      )}

      {rows.length > 0 && (
        <>
          <Table.ScrollContainer minWidth={960}>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Th label={t.worklist.columns.status} k="state" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={175} />
                  <Th label={t.worklist.columns.customer} k="asiakas" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label={t.worklist.columns.card} k="korttinumero" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label={t.worklist.columns.charges} k="charges" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={110} align="right" />
                  <Th label={t.worklist.columns.invoice} k="invoiceState" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={110} />
                  <Th label={t.worklist.columns.payment} k="paymentState" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={110} />
                  <Th label={t.worklist.columns.comment} />
                  <Th label={t.worklist.columns.lastAt} k="lastAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={140} />
                  <Th label={t.worklist.columns.firstAt} k="firstAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} w={140} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr
                    key={r.id}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openRow(r)}
                    onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRow(r); } }}
                  >
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <StateBadge status={r.status} closedReason={r.closedReason} />
                        {isNew(r, newSince) && (
                          <Badge color="teal" size="xs" radius="sm" title={t.worklist.newBadgeTitle(formatDateTime(r.createdAt))}>
                            {t.worklist.newBadge}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{r.asiakas ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {/* Fixed slot for the type chip so the number never shifts between rows. */}
                        <Box w={104} style={{ flexShrink: 0 }}><CardTypeBadge type={r.cardType} /></Box>
                        <Code>{r.korttinumero}</Code>
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">{r.charges}</Table.Td>
                    <Table.Td><ChargeStateBadge state={r.invoiceState} /></Table.Td>
                    <Table.Td><ChargeStateBadge state={r.paymentState} /></Table.Td>
                    <Table.Td maw={240}>
                      <Text size="xs" lineClamp={1} title={r.comment || undefined}>{r.comment || '—'}</Text>
                    </Table.Td>
                    <Table.Td>{formatDateTime(r.lastAt)}</Table.Td>
                    <Table.Td>{formatDateTime(r.firstAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">{t.worklist.showing(rows.length, total)}</Text>
            <Group gap="xs">
              <Button size="compact-sm" variant="default" disabled={page <= 1}
                onClick={() => patch({ page: page - 1 })}>{t.worklist.prev}</Button>
              <Text size="sm">{page} / {lastPage}</Text>
              <Button size="compact-sm" variant="default" disabled={page >= lastPage}
                onClick={() => patch({ page: page + 1 })}>{t.worklist.next}</Button>
            </Group>
          </Group>
        </>
      )}
    </Stack>
  );
}

function Th({ label, k, sortKey, sortDir, onSort, w, align }: {
  label: string; k?: SortKey; sortKey?: SortKey; sortDir?: 'asc' | 'desc'; onSort?: (k: SortKey) => void; w?: number; align?: 'right';
}): ReactNode {
  const ta = align === 'right' ? { textAlign: 'right' as const } : undefined;
  if (!k || !onSort) return <Table.Th w={w} style={ta}>{label}</Table.Th>;
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    // Keep the implicit columnheader role — aria-sort is only valid there.
    <Table.Th
      w={w}
      tabIndex={0}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={{ cursor: 'pointer', userSelect: 'none', ...ta }}
      onClick={() => onSort(k)}
      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(k); } }}
    >
      {label}{arrow}
    </Table.Th>
  );
}
